/**
 * Parser for the wide, denormalized "Active Clients" onboarding sheet.
 *
 * Each row is one client carrying, inline: the client fields, commercial
 * terms, a Main POC, an Account Manager + several Team Members, and up to a
 * few Task blocks (title + priority/status/dues/assignee/notes).
 *
 * Headers REPEAT across task blocks (priority/status/… appear once per task),
 * so this parses by ANCHORED POSITION, not by header name.
 */

import type { ParsedTable } from '../intake/types'
import { realignTable } from '../intake/parse'

// ─── Output shapes ───────────────────────────────────────────────

export interface OnboardPerson {
  name: string
  role: 'account_manager' | 'contributor'
}

export interface OnboardTask {
  title: string
  priority?: string
  status?: string
  due?: string
  assignedTo?: string
  reviewBy?: string
  notes?: string
}

export interface OnboardContract {
  term?: string
  startDate?: string
  billing?: string
  terms?: string
}

export interface OnboardRecord {
  sourceRow: number
  client: { name: string; mrr?: number; health?: string; notes?: string }
  contract?: OnboardContract
  poc?: { name: string; email?: string; phone?: string }
  team: OnboardPerson[]
  tasks: OnboardTask[]
  errors: string[]
}

export interface OnboardParse {
  records: OnboardRecord[]
  /** Distinct person names across account managers, team members, assignees. */
  people: string[]
}

// ─── Header helpers ──────────────────────────────────────────────

const norm = (h: string) => h.toLowerCase().replace(/[\s_\-./]+/g, '').replace(/[^a-z0-9]/g, '')

interface Layout {
  client: number
  mrr: number
  health: number
  notes: number
  scopeStart: number
  billing: number
  terms: number
  cadence: number
  poc: number
  accountManager: number
  teamMembers: number[]
  taskBlocks: { title: number; fields: Record<string, number> }[]
}

const TASK_FIELD_KEYS: Record<string, keyof OnboardTask> = {
  priority: 'priority',
  status: 'status',
  internaldue: 'due',
  duetoclient: 'due',
  reviewby: 'reviewBy',
  assignedto: 'assignedTo',
  notes: 'notes',
}

function analyzeHeaders(headers: string[]): Layout {
  const idxOf = (want: string) => headers.findIndex((h) => norm(h) === want)
  const teamMembers: number[] = []
  headers.forEach((h, i) => {
    if (/^teammember/.test(norm(h))) teamMembers.push(i)
  })

  // Task title columns: headers like "Task 1", "Task 2", …
  const taskTitleCols: number[] = []
  headers.forEach((h, i) => {
    if (/^task\d*$/.test(norm(h))) taskTitleCols.push(i)
  })

  const taskBlocks = taskTitleCols.map((title, bi) => {
    const end = taskTitleCols[bi + 1] ?? headers.length
    const fields: Record<string, number> = {}
    for (let c = title + 1; c < end; c++) {
      // CSV exports prefix sub-columns ("Task 1 - priority"); xlsx keeps them
      // bare ("priority"). Strip any task-N prefix so both forms resolve.
      const key = TASK_FIELD_KEYS[norm(headers[c] ?? '').replace(/^task\d*/, '')]
      if (key && !(key in fields)) fields[key] = c // first wins (internal due before due-to-client)
    }
    return { title, fields }
  })

  return {
    client: idxOf('client') !== -1 ? idxOf('client') : 0,
    mrr: idxOf('mrr'),
    health: idxOf('health'),
    notes: idxOf('notes'),
    scopeStart: idxOf('scopestartdate'),
    billing: idxOf('billingcycle'),
    terms: idxOf('terms'),
    cadence: idxOf('callcadence'),
    poc: idxOf('mainpoc'),
    accountManager: idxOf('accountmanager'),
    teamMembers,
    taskBlocks,
  }
}

// ─── Value coercion ──────────────────────────────────────────────

const cell = (row: string[], i: number) => (i >= 0 ? String(row[i] ?? '').trim() : '')

function money(v: string): number | undefined {
  const c = v.replace(/[$,\s]/g, '').replace(/\/mo$/i, '')
  if (!c) return undefined
  const n = Number(c)
  return Number.isFinite(n) ? Math.round(n) : undefined
}

function health(v: string): string | undefined {
  const s = v.toLowerCase().trim()
  if (!s) return undefined
  if (['good', 'watch', 'at-risk'].includes(s)) return s
  if (['healthy', 'green', 'ok', 'stable'].includes(s)) return 'good'
  if (['watch', 'amber', 'yellow', 'warning'].includes(s)) return 'watch'
  if (['at risk', 'atrisk', 'red', 'critical'].includes(s)) return 'at-risk'
  return undefined
}

export function taskStatus(v: string): string | undefined {
  const s = v.toLowerCase().trim()
  if (!s) return undefined
  if (['todo', 'in_progress', 'review', 'done', 'blocked'].includes(s)) return s
  if (['to do', 'backlog', 'new', 'open', 'not started'].includes(s)) return 'todo'
  if (['in progress', 'doing', 'wip', 'started', 'active'].includes(s)) return 'in_progress'
  if (['review', 'in review', 'qa', 'client review'].includes(s)) return 'review'
  if (['done', 'complete', 'completed', 'closed', 'shipped'].includes(s)) return 'done'
  if (['blocked', 'on hold', 'waiting', 'stuck'].includes(s)) return 'blocked'
  return undefined
}

export function taskPriority(v: string): string | undefined {
  const s = v.toLowerCase().trim()
  if (!s) return undefined
  if (['low', 'med', 'high', 'urgent'].includes(s)) return s
  if (['medium', 'normal', 'moderate'].includes(s)) return 'med'
  if (['critical', 'p0', 'asap'].includes(s)) return 'urgent'
  if (['p1'].includes(s)) return 'high'
  return undefined
}

function toDate(v: string): string | undefined {
  const s = v.trim()
  if (!s) return undefined
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10)
}

function term(billing: string, terms: string): string | undefined {
  const s = norm(billing) || norm(terms)
  if (!s) return undefined
  if (s.includes('month')) return 'monthly'
  if (s.includes('quarter')) return 'quarterly'
  if (s.includes('annual') || s.includes('year')) return 'annual'
  if (s.includes('oneoff') || s.includes('onetime') || s.includes('fixed') || s.includes('project')) return 'fixed'
  return undefined
}

/** Pull an email and/or phone out of a free-text POC cell; the rest is the name. */
function parsePoc(v: string): { name: string; email?: string; phone?: string } | undefined {
  const s = v.trim()
  if (!s) return undefined
  const email = s.match(/[\w.+-]+@[\w-]+\.[\w.-]+/)?.[0]
  const phone = s.match(/\+?\d[\d\s().-]{7,}\d/)?.[0]
  let name = s
  if (email) name = name.replace(email, '')
  if (phone) name = name.replace(phone, '')
  name = name.replace(/[·|,;<>()\-–—]+/g, ' ').replace(/\s+/g, ' ').trim()
  return { name: name || s, ...(email ? { email } : {}), ...(phone ? { phone: phone.trim() } : {}) }
}

// ─── Main ────────────────────────────────────────────────────────

export function parseWide(raw: ParsedTable): OnboardParse {
  // Skip banner/preamble rows above the real header (common in xlsx exports).
  const table = realignTable(raw, ['client'], ['accountmanager', 'teammember1', 'mainpoc', 'mrr'])
  const L = analyzeHeaders(table.headers)
  const people = new Set<string>()
  const addPerson = (n: string) => {
    const t = n.trim()
    if (t) people.add(t)
  }

  const records: OnboardRecord[] = table.rows.map((row, ri) => {
    const errors: string[] = []
    const name = cell(row, L.client)

    const client = {
      name,
      mrr: money(cell(row, L.mrr)),
      health: health(cell(row, L.health)),
      notes: cell(row, L.notes) || undefined,
    }
    if (!name) errors.push('Client name is missing')

    // Contract (only if any commercial field present)
    const billing = cell(row, L.billing)
    const terms = cell(row, L.terms)
    const start = toDate(cell(row, L.scopeStart))
    const contract =
      billing || terms || start
        ? { term: term(billing, terms), startDate: start, billing: billing || undefined, terms: terms || undefined }
        : undefined

    // POC → contact
    const poc = parsePoc(cell(row, L.poc))

    // Team
    const team: OnboardPerson[] = []
    const am = cell(row, L.accountManager)
    if (am) {
      team.push({ name: am, role: 'account_manager' })
      addPerson(am)
    }
    for (const tmIdx of L.teamMembers) {
      const tm = cell(row, tmIdx)
      if (tm) {
        team.push({ name: tm, role: 'contributor' })
        addPerson(tm)
      }
    }

    // Tasks
    const tasks: OnboardTask[] = []
    for (const block of L.taskBlocks) {
      const title = cell(row, block.title)
      if (!title) continue
      const t: OnboardTask = { title }
      const f = block.fields
      if (f.priority !== undefined) t.priority = taskPriority(cell(row, f.priority))
      if (f.status !== undefined) t.status = taskStatus(cell(row, f.status))
      if (f.due !== undefined) t.due = toDate(cell(row, f.due))
      if (f.notes !== undefined) t.notes = cell(row, f.notes) || undefined
      const assignedTo = f.assignedTo !== undefined ? cell(row, f.assignedTo) : ''
      const reviewBy = f.reviewBy !== undefined ? cell(row, f.reviewBy) : ''
      if (assignedTo) { t.assignedTo = assignedTo; addPerson(assignedTo) }
      if (reviewBy) { t.reviewBy = reviewBy; addPerson(reviewBy) }
      tasks.push(t)
    }

    return { sourceRow: ri + 2, client, contract, poc, team, tasks, errors }
  })

  return { records, people: [...people].sort() }
}
